/**
 * Feature Flags Integration
 *
 * Provides feature flag utilities using PostHog.
 * Re-exports hooks from posthog-js/react for convenience.
 */

/* eslint-disable react-refresh/only-export-components */

import { useFeatureFlagEnabled, useFeatureFlagPayload, usePostHog } from 'posthog-js/react';
import type { ReactNode } from 'react';

// ============================================================================
// FEATURE FLAG KEYS
// Define all feature flags here for type safety
// ============================================================================

export const FeatureFlags = {
  // Active feature flags
  INSTRUCTOR_STRIPE_CONNECT: 'instructor-stripe-connect',
  ENABLE_STUDIO_CLASSES: 'enable-studio-classes',
  MONEY_CENTS_MIGRATION: 'money-cents-migration',

  // Future/placeholder flags
  NEW_BOOKING_FLOW: 'new-booking-flow',
  DARK_MODE: 'dark-mode',
  BETA_ANALYTICS: 'beta-analytics',
} as const;

export type FeatureFlagKey = (typeof FeatureFlags)[keyof typeof FeatureFlags];

// ============================================================================
// HOOKS - Re-export from posthog-js/react with our naming
// ============================================================================

/**
 * Check if a specific feature flag is enabled.
 *
 * @param flagKey - The feature flag key to check
 * @returns boolean indicating if the flag is enabled
 *
 * @example
 * const isNewFlowEnabled = useFeatureFlag('new-booking-flow');
 * if (isNewFlowEnabled) {
 *   return <NewBookingFlow />;
 * }
 */
// Dev overrides — force specific flags on/off locally without PostHog
const DEV_FLAG_OVERRIDES: Partial<Record<string, boolean>> = import.meta.env.DEV ? {
  [FeatureFlags.ENABLE_STUDIO_CLASSES]: true,
} : {};

export function useFeatureFlag(flagKey: string): boolean {
  const enabled = useFeatureFlagEnabled(flagKey);
  if (flagKey in DEV_FLAG_OVERRIDES) return DEV_FLAG_OVERRIDES[flagKey]!;
  return enabled ?? false;
}

/**
 * Get the value of a multivariate feature flag.
 *
 * @param flagKey - The feature flag key
 * @returns The flag value
 *
 * @example
 * const variant = useFeatureFlagValue('checkout-variant');
 * // variant could be 'control', 'variant-a', 'variant-b', etc.
 */
export function useFeatureFlagValue(flagKey: string): string | boolean | undefined {
  const posthog = usePostHog();
  return posthog?.getFeatureFlag(flagKey);
}

/**
 * Get the payload attached to a feature flag.
 *
 * @param flagKey - The feature flag key
 * @returns The payload data
 *
 * @example
 * const config = useFeatureFlagPayloadValue('pricing-test') as { price: number };
 */
export function useFeatureFlagPayloadValue<T = unknown>(flagKey: string): T | undefined {
  const payload = useFeatureFlagPayload(flagKey);
  return payload as T | undefined;
}

/**
 * Get the PostHog instance for advanced usage.
 * Returns undefined if PostHog is not initialized.
 */
export { usePostHog };

// ============================================================================
// COMPONENT
// ============================================================================

interface FeatureProps {
  /** The feature flag key to check */
  flag: string;
  /** Content to show when flag is enabled */
  children: ReactNode;
  /** Optional content to show when flag is disabled */
  fallback?: ReactNode;
}

/**
 * Conditionally render content based on a feature flag.
 *
 * @example
 * <Feature flag="new-feature">
 *   <NewFeatureComponent />
 * </Feature>
 *
 * @example
 * <Feature flag="beta-feature" fallback={<OldComponent />}>
 *   <BetaComponent />
 * </Feature>
 */
export function Feature({ flag, children, fallback = null }: FeatureProps) {
  const enabled = useFeatureFlag(flag);
  return <>{enabled ? children : fallback}</>;
}

// ============================================================================
// UTILITY FUNCTIONS (for non-React contexts)
// ============================================================================

/**
 * Track a custom event.
 * Use this for tracking outside of React components.
 *
 * @param eventName - Name of the event to track
 * @param properties - Optional properties to attach to the event
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, string | number | boolean | null | undefined>
): void {
  // Access posthog from window in non-React contexts
  const posthog = typeof window !== 'undefined' ? (window as unknown as { posthog?: { capture: (name: string, props?: unknown) => void } }).posthog : undefined;
  if (posthog) {
    posthog.capture(eventName, properties);
  }
}

/**
 * Identify a user for personalized analytics and feature flags.
 * Use this for identification outside of React components.
 *
 * @param userId - Unique user identifier
 * @param properties - Optional user properties
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, string | number | boolean | null>
): void {
  const posthog = typeof window !== 'undefined' ? (window as unknown as { posthog?: { identify: (id: string, props?: unknown) => void; reloadFeatureFlags: () => void } }).posthog : undefined;
  if (posthog) {
    posthog.identify(userId, properties);
    // Reload feature flags after identifying user to get user-specific flags
    posthog.reloadFeatureFlags();
  }
}

/**
 * Reset user identity (call on logout).
 * Use this for reset outside of React components.
 */
export function resetUser(): void {
  const posthog = typeof window !== 'undefined' ? (window as unknown as { posthog?: { reset: () => void } }).posthog : undefined;
  if (posthog) {
    posthog.reset();
  }
}
